/**
 * LaGift — footer accordions
 * ---------------------------------------------------------------------------
 * The footer's collapsible link menus are native <details> elements, so they
 * already open and close with no JavaScript. This adds only the slide, because
 * <details> cannot be transitioned on its own.
 *
 * Progressive enhancement in the strict sense: if this file never loads, the
 * accordions still work, just without the animation.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS DOES NOT MEASURE ANYTHING
 * ---------------------------------------------------------------------------
 * The motion is a CSS transition on `grid-template-rows: 0fr -> 1fr`, driven by
 * one class. This file only decides when that class is on.
 *
 * An earlier version animated the panel's height with the Web Animations API,
 * reading scrollHeight on every click. It closed smoothly but opened in one
 * jump: a closed <details> renders no content, so the instant `open` flipped the
 * box was laid out at full height, and the animation only took effect on the
 * following frame. Every fix for that is a fight with frame ordering. Handing
 * the interpolation to CSS removes the problem rather than timing around it.
 *
 * The only ordering that still matters is on open: `open` has to be set first so
 * the content exists, then the class one frame later so there is a `0fr` state
 * to transition from.
 */
/**
 * Scoped so the file can run more than once.
 *
 * A `<script src>` executes once per tag, not once per URL. Several sections
 * include this, and a merchant can add the same section twice, so on a busy
 * page it runs repeatedly - and a top-level class or const throws
 * "Identifier ... has already been declared" on every run after the first.
 * Inside a function there is nothing shared to collide with.
 */
(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DESKTOP = window.matchMedia('(min-width: 750px)');

  // Matches --lg-duration, with headroom. Only a safety net: the transitionend
  // listener normally closes the panel well before this fires.
  const CLOSE_FALLBACK = 500;

  function setup(details) {
    if (details.dataset.lagiftAccordion === 'ready') return;
    details.dataset.lagiftAccordion = 'ready';

    const summary = details.querySelector('summary');
    const box = details.querySelector('.lagift-footer__accordion-content');
    if (!summary || !box) return;

    // Keep the class in step with any panel that starts open.
    details.classList.toggle('is-expanded', details.open);

    let closeTimer = null;

    const finishClose = () => {
      clearTimeout(closeTimer);
      box.removeEventListener('transitionend', onCollapsed);
      details.open = false;
    };

    /**
     * transitionend bubbles, so a link's own colour transition inside the panel
     * would otherwise close it mid-slide. Both the target and the property have
     * to be checked.
     */
    const onCollapsed = (event) => {
      if (event.target === box && event.propertyName === 'grid-template-rows') finishClose();
    };

    summary.addEventListener('click', (event) => {
      // Above 750px the accordion is display:none and the plain list is shown, so
      // there is nothing to animate and the native behaviour is correct.
      if (DESKTOP.matches || REDUCED_MOTION.matches) return;

      event.preventDefault();
      clearTimeout(closeTimer);
      box.removeEventListener('transitionend', onCollapsed);

      if (details.classList.contains('is-expanded')) {
        details.classList.remove('is-expanded');
        // `open` stays true until the row has finished collapsing, or the content
        // would vanish instantly instead of sliding away.
        box.addEventListener('transitionend', onCollapsed);
        closeTimer = setTimeout(finishClose, CLOSE_FALLBACK);
      } else {
        // Content must exist before there is anything to transition.
        details.open = true;
        requestAnimationFrame(() => details.classList.add('is-expanded'));
      }
    });
  }

  function setupAll(root = document) {
    root.querySelectorAll('.lagift-footer__accordion').forEach(setup);
  }

  setupAll();

  // The theme editor re-renders a section without a full page load.
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
